import React, { useState, useCallback } from 'react';
import { Form, Button, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import SearchForm from './components/SearchForm';
import StyleTable from './components/StyleTable';
import StyleDrawer from './components/StyleDrawer';
import { getStyleList } from './api';
import { StyleSearchParams, StyleRecord } from './types';
import { QUERY_KEYS } from './constants';

const DEFAULT_PAGE_SIZE = 10;

const TextToImageStylePage: React.FC = () => {
  const [form] = Form.useForm<StyleSearchParams>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingStyle, setEditingStyle] = useState<StyleRecord | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: [QUERY_KEYS.STYLE_LIST, currentPage, pageSize, form.getFieldsValue()],
    queryFn: () =>
      getStyleList({
        ...form.getFieldsValue(),
        pageNum: currentPage,
        pageSize,
      }),
  });

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    refetch();
  }, [refetch]);

  const handleReset = useCallback(() => {
    form.resetFields();
    setCurrentPage(1);
    refetch();
  }, [form, refetch]);

  const handleTableChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    }
  }, [pageSize]);

  const openDrawer = useCallback((style: StyleRecord | null = null) => {
    setEditingStyle(style);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setEditingStyle(null);
  }, []);

  const handleDrawerSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <SearchForm
          form={form}
          onSearch={handleSearch}
          onReset={handleReset}
        />
        <Button type="primary" onClick={() => openDrawer(null)}>
          新增
        </Button>
      </Space>

      <StyleTable
        data={data?.records || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.total || 0,
          onChange: handleTableChange,
        }}
        onEdit={openDrawer}
      />

      <StyleDrawer
        open={drawerOpen}
        editingStyle={editingStyle}
        onClose={closeDrawer}
        onSuccess={handleDrawerSuccess}
      />
    </div>
  );
};

export default TextToImageStylePage;
